import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowRightIcon, CheckIcon, Loader2Icon, ShieldCheckIcon, TruckIcon } from 'lucide-react';
import { GoldMedal } from './LifetimeSeal';
import { money } from './QuantityStepper';
import type { UpsellStep, OrderSummary } from '../data/upsellFlows';
import type { ActiveConfirmation } from '../hooks/useUpsellFlow';
interface UpsellModalProps {
  step: UpsellStep | null;
  /** Shown after an accepted offer that confirms before checkout. */
  confirmation?: ActiveConfirmation | null;
  onContinue?: () => void;
  onFollowUp?: () => void;
  /** True while the customer is being routed to the Shopify cart. */
  routing: boolean;
  /** The chosen product has no Shopify link yet. */
  missingLink?: boolean;
  onAccept: () => void;
  /** The smaller offer shown on the same screen, when the step has one. */
  onAcceptAlternate?: () => void;
  onDecline: () => void;
  /** Close without choosing: stays on the page, nothing is added. */
  onClose: () => void;
}
const ease = [0.23, 1, 0.32, 1] as const;

/** Coverage and shipping status for one side of the comparison. */
function OrderStatus({
  summary,
  highlight = false
}: {
  summary: OrderSummary;
  highlight?: boolean;
}) {
  return <ul className="mt-2 grid gap-1 text-[11px] font-bold uppercase leading-tight tracking-wide" data-id="element-1466" data-mp="8jpskoah0hd3">
      <li className={`flex items-start gap-1.5 ${highlight ? 'text-[#ffe79a]' : 'text-steel-300'}`} data-id="element-1467" data-mp="di9fos0svz71">
        <ShieldCheckIcon className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-1468" data-mp="3c0eo7f8im6nz" />
        {summary.coverage}
      </li>
      <li className={`flex items-start gap-1.5 ${summary.freeShipping ? highlight ? 'text-grit-500' : 'text-steel-300' : 'text-steel-500'}`} data-id="element-1469" data-mp="3m0oyos360zzg">
        <TruckIcon className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-1470" data-mp="2tbrjwyy5xlbg" />
        Free U.S. shipping
      </li>
    </ul>;
}

/**
 * One negotiation step. Always shows what the customer has now, what the offer
 * is, the exact extra cost and the new total, with a visible way to say no.
 */
export function UpsellModal({
  step,
  confirmation = null,
  onContinue,
  onFollowUp,
  routing,
  missingLink,
  onAccept,
  onAcceptAlternate,
  onDecline,
  onClose
}: UpsellModalProps) {
  const acceptRef = useRef<HTMLButtonElement>(null);
  const open = Boolean(step || confirmation);
  useEffect(() => {
    if (!open) return;
    acceptRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !routing) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, step, routing, onClose]);
  return <AnimatePresence data-id="element-1471" data-mp="2c9ucr3gs3f0s">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-ink-950/85 backdrop-blur-sm sm:items-center sm:p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.16,
      ease
    }} role="dialog" aria-modal="true" aria-labelledby="upsell-heading" data-id="element-1472" data-mp="2c92ipx2txpcm">
          <motion.div key={step ? step.id : 'confirmation'} className="relative max-h-[92vh] w-full max-w-xl overflow-y-auto border-2 gold-edge bg-ink-950 shadow-[0_0_48px_rgba(242,201,76,0.14)]" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 8
      }} transition={{
        duration: 0.22,
        ease
      }} data-id="element-1473" data-mp="1ei5vw4cwlb0r">
            <div className="gold-plate flex items-center justify-between gap-3 px-5 py-2.5" data-id="element-1474" data-mp="1jgh64g741nji">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-ink-950" data-id="element-1475" data-mp="1oeshwwgyu84d">
                {step ? step.plate : 'Offer claimed'}
              </p>
              <button type="button" onClick={onClose} disabled={routing} aria-label="Close" className="-mr-2 flex h-8 w-8 items-center justify-center text-ink-950 transition-opacity duration-150 ease-grit hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-950" data-id="element-1476" data-mp="2c2js9uiy4b30">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-1477" data-mp="j93wtpzfhm60" />
              </button>
            </div>

            {!step && confirmation && <div className="p-5 sm:p-7" data-id="element-1478" data-mp="1b0jhvflc70u4">
                <div className="flex items-center gap-4" data-id="element-1479" data-mp="1azi8rgnvsof1">
                  <GoldMedal size={72} data-id="element-1480" data-mp="16xwo3953v5ua" />
                  <h2 id="upsell-heading" className="grit-headline text-[clamp(1.85rem,6.5vw,2.75rem)] font-extrabold uppercase leading-[0.95] gold-text" data-id="element-1481" data-mp="16yy6xzsd4wmk">
                    {confirmation.headline}
                  </h2>
                </div>
                <p className="mt-4 text-[15px] font-semibold leading-relaxed text-steel-200" data-id="element-1482" data-mp="1upbsr8kctz8p">{confirmation.body}</p>
                {missingLink && <p role="alert" className="mt-4 border border-white/20 bg-white/[0.04] px-3 py-2.5 text-[13px] text-steel-200" data-id="element-1483" data-mp="1x8yncnxdw9mu">
                    This checkout link isn&apos;t connected yet. Please try again shortly or email support@gritvuegear.com.
                  </p>}
                <button ref={acceptRef} type="button" onClick={onContinue} disabled={routing} className="mt-6 flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite sm:text-2xl" data-id="element-1484" data-mp="2b7yafr590ggw">
                  {routing ? <>
                      <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1485" data-mp="33xx645dy7buj" />
                      Taking you to checkout
                    </> : <>
                      {confirmation.cta}
                      <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1486" data-mp="zr7rejt4y7xy" />
                    </>}
                </button>
                {confirmation.followUp && <button type="button" onClick={onFollowUp} disabled={routing} className="mt-4 inline-flex w-full items-center justify-center gap-1.5 text-center text-[13px] font-bold uppercase tracking-wide text-steel-300 underline-offset-4 transition-colors duration-150 ease-grit hover:text-grit-500 hover:underline disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-1487" data-mp="18rme9nlqzv3k">
                    {confirmation.followUp.label}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1488" data-mp="3tcmzfs1j3sel" />
                  </button>}
                <p className="mt-4 text-center text-[11px] text-steel-500" data-id="element-1489" data-mp="28u2ms1vs5d2g">Taxes, if applicable, calculated at checkout.</p>
              </div>}

            {step && <div className="p-5 sm:p-7" data-id="element-1490" data-mp="2006632ldwsuf">
              <h2 id="upsell-heading" className="grit-headline text-[clamp(1.85rem,6.5vw,3rem)] font-extrabold uppercase leading-[0.95] text-offwhite" data-id="element-1491" data-mp="1zz4p6hhotc4s">
                {step.headline}
                <br data-id="element-1492" data-mp="1v2ns2xsifnfk" />
                <span className="gold-text" data-id="element-1493" data-mp="1v35w4glez5y1">{step.highlight}</span>
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-steel-200" data-id="element-1494" data-mp="1h34x76opm8l7">{step.intro}</p>

              {/* Current vs. new, side by side even on phones: totals are the first thing read. */}
              <div className="mt-5 grid grid-cols-2 gap-2 sm:gap-3" data-id="element-1495" data-mp="3bfuttw6gpvwa">
                <div className="border border-white/12 bg-white/[0.03] p-3 sm:p-4" data-id="element-1496" data-mp="3bejq1wd0oo56">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-1497" data-mp="3ffvg22zszbdw">Your selection</p>
                  <p className="mt-1.5 font-display text-base font-bold uppercase leading-tight text-offwhite sm:text-lg" data-id="element-1498" data-mp="1y9je66cijfyq">
                    {step.current.label}
                  </p>
                  <p className="mt-0.5 text-[12px] leading-snug text-steel-400" data-id="element-1499" data-mp="1tgq89f5b61l2">{step.current.detail}</p>
                  <OrderStatus summary={step.current} data-id="element-1500" data-mp="1ohvrsjutcp44" />
                  <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-1501" data-mp="1kiwf2d2ubo24">
                    Current product total
                  </p>
                  <p className="font-display text-3xl font-extrabold leading-none text-offwhite" data-id="element-1502" data-mp="1ajp602352fkt">
                    {money(step.current.total)}
                  </p>
                </div>
                <div className="border-2 border-grit-500 bg-grit-500/[0.07] p-3 sm:p-4" data-id="element-1503" data-mp="34xzug9ftn7q2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-grit-500" data-id="element-1504" data-mp="2zyvjped911xm">Upgrade</p>
                  <p className="mt-1.5 font-display text-base font-bold uppercase leading-tight text-offwhite sm:text-lg" data-id="element-1505" data-mp="38fvxzsl8j387">
                    {step.upgrade.label}
                  </p>
                  <p className="mt-0.5 text-[12px] leading-snug text-steel-300" data-id="element-1506" data-mp="3cf4o8nh2giz0">{step.upgrade.detail}</p>
                  <OrderStatus summary={step.upgrade} highlight data-id="element-1507" data-mp="3m7apknt7kcqv" />
                  <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.18em] text-grit-500" data-id="element-1508" data-mp="3m6shmtx6oa34">
                    New product total
                  </p>
                  <p className="font-display text-3xl font-extrabold leading-none text-grit-500" data-id="element-1509" data-mp="2thv4143x31gp">
                    {money(step.upgrade.total)}
                  </p>
                </div>
              </div>
              <p className="mt-2 bg-grit-500 px-4 py-2 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950" data-id="element-1510" data-mp="33hubexololrz">
                {step.extra}
              </p>

              {step.economics && <dl className="mt-3 grid gap-1 border border-white/10 px-4 py-3 text-[13px]" data-id="element-1511" data-mp="ioquzen24usw">
                  <div className="flex justify-between gap-3 text-steel-300" data-id="element-1512" data-mp="1zvigryyqw33s">
                    <dt data-id="element-1513" data-mp="24u3adfjhroj9">Product price difference</dt>
                    <dd className="font-semibold text-offwhite" data-id="element-1514" data-mp="29ltn1s5wiodu">{step.economics.productDiff}</dd>
                  </div>
                  <div className="flex justify-between gap-3 border-t border-white/10 pt-1.5 font-bold uppercase tracking-wide text-grit-500" data-id="element-1515" data-mp="2dnydyj4yt6ws">
                    <dt data-id="element-1516" data-mp="1gyqs1a0fovsc">Expected difference, before tax</dt>
                    <dd data-id="element-1517" data-mp="1lvqy3kxa9yjg">{step.economics.result}</dd>
                  </div>
                </dl>}

              {step.valueLine && <dl className="mt-4 grid grid-cols-3 border border-white/10 text-center" data-id="element-1518" data-mp="1pwj8ihhw69hb">
                  <div className="p-2.5" data-id="element-1519" data-mp="1uyi9jkq5sub3">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-steel-400" data-id="element-1520" data-mp="31d9imbpfrlg7">Normal value</dt>
                    <dd className="mt-0.5 font-display text-lg font-bold text-steel-400 line-through" data-id="element-1521" data-mp="36bb22xqrxms7">
                      {step.valueLine.normal}
                    </dd>
                  </div>
                  <div className="border-x border-white/10 p-2.5" data-id="element-1522" data-mp="3bgfzmdo8nqcb">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-grit-500" data-id="element-1523" data-mp="3bjajo43cb957">
                      {step.valueLine.todayLabel}
                    </dt>
                    <dd className="mt-0.5 font-display text-lg font-bold text-grit-500" data-id="element-1524" data-mp="32odzkgi30c2k">{step.valueLine.today}</dd>
                  </div>
                  <div className="p-2.5" data-id="element-1525" data-mp="2sqq1bgl9omfn">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-steel-400" data-id="element-1526" data-mp="2sponx5gs3nur">Free shipping</dt>
                    <dd className="mt-0.5 font-display text-lg font-bold text-offwhite" data-id="element-1527" data-mp="2nrn5mmr6fskc">{step.valueLine.shipping}</dd>
                  </div>
                </dl>}

              <div className="mt-4 flex items-start gap-4" data-id="element-1528" data-mp="lypcsqxegzze">
                {step.lifetimeMedal && <div className="hidden shrink-0 sm:block" data-id="element-1529" data-mp="hywyryp8m1mn">
                    <GoldMedal size={78} data-id="element-1530" data-mp="2bgm4ifns3i3k" />
                  </div>}
                <div data-id="element-1531" data-mp="2g7uizq3yttnl">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-1532" data-mp="2g846n71jfoq4">You get</p>
                  <ul className="mt-2 grid gap-1.5 text-[14px] font-semibold text-steel-200" data-id="element-1533" data-mp="22hh93i7ik4vm">
                    {step.includes.map(line => <li key={line} className="flex items-start gap-2" data-id="element-1534" data-mp="26fopud1z966t">
                        <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1535" data-mp="2u8574ldw4u52" />
                        {line}
                      </li>)}
                  </ul>
                </div>
              </div>

              <p className="mt-4 border-l-2 border-grit-500 pl-3 text-[13px] font-bold uppercase tracking-wide text-offwhite" data-id="element-1536" data-mp="2q5s4w6ycred1">
                {step.clarification}
              </p>

              {missingLink && <p role="alert" className="mt-4 border border-white/20 bg-white/[0.04] px-3 py-2.5 text-[13px] text-steel-200" data-id="element-1537" data-mp="1er53idzqqk87">
                  This checkout link isn&apos;t connected yet. Please try again shortly or email support@gritvuegear.com.
                </p>}

              <button ref={acceptRef} type="button" onClick={onAccept} disabled={routing} className="mt-5 flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite sm:text-2xl" data-id="element-1538" data-mp="1g8k8xhogcpx3">
                {routing ? <>
                    <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1539" data-mp="4s3rxjx92184" />
                    Taking you to checkout
                  </> : <>
                    {step.accept}
                    <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1540" data-mp="2z9cmmeizodsm" />
                  </>}
              </button>
              {step.alternate && onAcceptAlternate && <div className="mt-5 border border-white/15 bg-white/[0.03] p-4" data-id="element-1541" data-mp="386wtq43j94b5">
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-1542" data-mp="3d07zs30ith20">{step.alternate.eyebrow}</p>
                  <div className="mt-1.5 flex items-start justify-between gap-4" data-id="element-1543" data-mp="3hy17x1ldwl4x">
                    <div data-id="element-1544" data-mp="3mw2n3bfow0b4">
                      <p className="font-display text-lg font-bold uppercase leading-tight text-offwhite" data-id="element-1545" data-mp="3mzgrep7wyjl7">
                        {step.alternate.headline}
                      </p>
                      <p className="mt-1 text-[13px] leading-snug text-steel-300" data-id="element-1546" data-mp="3vxirn2jtp87f">{step.alternate.body}</p>
                      <OrderStatus summary={step.alternate.summary} highlight data-id="element-1547" data-mp="1xk8x379oad95" />
                    </div>
                    <div className="shrink-0 text-right" data-id="element-1548" data-mp="21m3yebrlsqmx">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-steel-400" data-id="element-1549" data-mp="1u1jw5dr6x4lj">New total</p>
                      <p className="font-display text-3xl font-extrabold leading-none text-offwhite" data-id="element-1550" data-mp="1p90adticn35n">
                        {money(step.alternate.summary.total)}
                      </p>
                    </div>
                  </div>
                  <p className="mt-3 text-[12px] font-bold uppercase tracking-wide text-steel-200" data-id="element-1551" data-mp="1g7ah0i4wpu58">
                    {step.alternate.extra} · {step.alternate.clarification}
                  </p>
                  <button type="button" onClick={onAcceptAlternate} disabled={routing} className="mt-3 flex w-full items-center justify-center gap-2 border-2 border-grit-500 px-4 py-3 text-center font-display text-lg font-extrabold uppercase tracking-wide text-grit-500 transition-[background-color,color] duration-150 ease-grit hover:bg-grit-500 hover:text-ink-950 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-1552" data-mp="27ygnrhf4uq3h">
                    {step.alternate.cta}
                    <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1553" data-mp="tyc1sgdd539k" />
                  </button>
                </div>}
              <button type="button" onClick={onDecline} disabled={routing} className="mt-2.5 w-full border border-white/20 px-4 py-3 text-center text-[13px] font-bold uppercase tracking-wide text-steel-300 transition-colors duration-150 ease-grit hover:border-white/40 hover:text-offwhite disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-1554" data-mp="k1qvzstiro4o">
                {step.decline}
              </button>
              <p className="mt-3 text-center text-[11px] leading-snug text-steel-500" data-id="element-1555" data-mp="3jiowy4yx2d5r">
                Free U.S. shipping on every order. Taxes, if applicable, calculated at checkout.
              </p>
            </div>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}